<template>
    <div v-show="booking">
        <full-width-container class="bg-r-green">
            <content-container padding="lg:py-4 lg:px-12 py-3">
                <stepper :current_step="current_step">
                    {{ $t("navigation.footer.book_roatel") }}
                </stepper>
            </content-container>
        </full-width-container>
        <full-width-container class="bg-r-gray-ultralight">
            <content-container>
                <div class="flex flex-col lg:flex-row gap-8">
                    <div class="w-full lg:w-1/3 flex flex-col gap-10">
                        <new-booking-summary-sidebar
                            :property="property"
                            :booking="booking"
                            :show-dates="true"
                            :show-room-count="true"
                            :show-total-amount="true"
                            :show-information="true"
                            :show-imprint-link="true"
                            :show-guests="true"
                            :show-booker="true"
                            :show-open-in-map="true"
                            :show-address="true"
                            :show-carousel="true"
                            :swiper-toggle-visible="true"
                            :swiper-initial-status="false"
                        >
                            <template v-slot:before>
                                <router-link
                                    class="group inline-flex gap-3 text-r-gray items-center"
                                    :to="{
                                        name: 'bookings.new.booker',
                                    }"
                                >
                                    <icon
                                        icon="arrow-narrow-left"
                                        class="stroke-r-gray w-6 h-6 group-hover:stroke-r-green"
                                    ></icon>
                                    <span
                                        class="group-hover:text-r-green leading-none"
                                        >{{
                                            $t("new_reservation.booker.heading")
                                        }}</span
                                    ></router-link
                                >
                            </template>
                        </new-booking-summary-sidebar>
                    </div>
                    <div
                        class="w-full lg:w-2/3 flex flex-col gap-10"
                        id="payment_container"
                    >
                        <div>
                            <div
                                class="bg-white lg:px-8 lg:py-16 px-3 py-2 rounded-lg border border-r-gray-light grid gap-8"
                            >
                                <h2
                                    class="text-xl lg:text-2xl italic pb-4 font-black text-center text-r-green py-7"
                                >
                                    {{ $t("new_reservation.payment.heading") }}
                                </h2>
                                <alert v-if="payment_refused" class="mb-6">
                                    {{
                                        $t(
                                            "new_reservation.payment.payment_refused"
                                        )
                                    }}
                                </alert>
                                <alert
                                    v-if="put_booking_errors.length > 0"
                                    class="mb-6"
                                >
                                    <div
                                        v-for="put_booking_error in put_booking_errors"
                                    >
                                        {{ put_booking_error }}
                                    </div>
                                </alert>
                                <alert v-if="payment_error" class="mb-6">
                                    {{
                                        $t(
                                            "new_reservation.payment.payment_error"
                                        )
                                    }}
                                </alert>
                                <alert
                                    v-if="booking_failed_after_payment"
                                    class="mb-6"
                                >
                                    {{
                                        $t(
                                            "new_reservation.payment.booking_failed_after_payment"
                                        )
                                    }}
                                </alert>

                                <div
                                    v-if="payment_starting"
                                    class="mb-6 flex flex-row items-center justify-center text-lg space-x-3 text-center p-4 rounded-lg bg-r-gray-ultralight text-r-green"
                                >
                                    <div>
                                        <font-awesome-icon
                                            icon="spinner"
                                            spin
                                        ></font-awesome-icon>
                                    </div>
                                    <div>
                                        {{
                                            $t(
                                                "new_reservation.payment.payment_starting"
                                            )
                                        }}
                                    </div>
                                </div>
                                <div
                                    v-if="payment_pending"
                                    class="mb-6 flex flex-row items-center justify-center text-lg space-x-3 text-center p-4 rounded-lg bg-r-gray-ultralight text-r-green"
                                >
                                    <div>
                                        <font-awesome-icon
                                            icon="spinner"
                                            spin
                                        ></font-awesome-icon>
                                    </div>
                                    <div>
                                        {{
                                            $t(
                                                "new_reservation.payment.payment_pending"
                                            )
                                        }}
                                    </div>
                                </div>
                                <div
                                    v-show="!booking_failed_after_payment"
                                    id="dropin-container"
                                    ref="adyen"
                                    class="min-h-[3rem]"
                                ></div>
                                <div class="flex justify-center py-4">
                                    <app-button
                                        v-if="
                                            !start_payment &&
                                            hasPaymentPending === false &&
                                            !payment_starting
                                        "
                                        @click="startPayment"
                                        color="green"
                                        size="xl"
                                        >{{
                                            $t(
                                                "new_reservation.payment.button_start_payment"
                                            )
                                        }}</app-button
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <property-imprint-modal ref="propertyImprintModal">
                </property-imprint-modal>
            </content-container>
        </full-width-container>
    </div>
</template>

<script>
import AppButton from "@guest/common/Button";
import DayJS from "@mixins/DayJS";
import BookingPaymentAmount from "@mixins/BookingPaymentAmount";

import ContentContainer from "@guest/common/ContentContainer";
import { mapState, mapActions, mapGetters } from "vuex";
import Currency from "@mixins/Currency";

import AdyenCheckout from "@adyen/adyen-web";
import "@adyen/adyen-web/dist/adyen.css";
import FullWidthContainer from "@guest/common/FullWidthContainer.vue";
import PropertyCard from "@guest/bookings/new/PropertyCard";
import Stepper from "@guest/bookings/new/Stepper";
import RequiredFieldInfo from "@guest/common/RequiredFieldInfo";
import BookingBackLink from "@guest/common/BookingBackLink.vue";
import BookingSummarySidebar from "@guest/bookings/new/BookingSummarySidebar.vue";
import Alert from "@guest/common/Alert.vue";
import PropertyImprintModal from "@guest/common/PropertyImprintModal.vue";
import BookingPropertyLabel from "@guest/common/BookingPropertyLabel.vue";
import NewBookingSummarySidebar from "@guest/common/NewBookingSummarySidebar.vue";
import Icon from "@guest/common/Icon";

export default {
    mixins: [DayJS, Currency, BookingPaymentAmount],
    components: {
        ContentContainer,
        AppButton,
        FullWidthContainer,
        PropertyCard,
        Stepper,
        RequiredFieldInfo,
        BookingBackLink,
        BookingSummarySidebar,
        Alert,
        PropertyImprintModal,
        BookingPropertyLabel,
        NewBookingSummarySidebar,
        Icon,
    },
    watch: {},
    data: function () {
        return {
            start_payment: false,
            initialized: true,
            errors: {},
            loading: false,
            payment_methods: [],
            payment_checkout: null,
            payment_dropin: null,
            amount: 10000,
            //payment_currency: 'EUR',
            payment_uuid: null,
            payment_result: null,
            payment_config: {},
            payment_pending: true,
            payment_error: false,
            payment_starting: false,
            payment_refused: false,
            payment_refused_code: "",
            put_booking_errors: [],
            submitted_payment_method_type: null,
            booking_failed_after_payment: false,
        };
    },
    computed: {
        ...mapState({
            booking: "bookingDraft",
            property: (state) => state.bookingDraft.property,
            offer: (state) => state.bookingDraft.offer,
            booker: (state) => state.bookingDraft.booker,
            dates: (state) => state.bookingDraft.dates,
            reservationsStore: (state) => state.bookingDraft.reservations,
            profile: (state) => state.profile,
        }),
        ...mapGetters({
            currentLocale: "currentLocale",
        }),
        // payment_amount() {
        //     console.log("Payment_Amound computed", this.booking.offer);
        //     if (this.booking.offer) {
        //         let amount = 0;
        //         console.log("calculateBooking", amount);
        //         //Rooms
        //         console.log("Calculating Booking Services before room", amount);
        //         amount +=
        //             this.booking.rooms *
        //             this.booking.offer.totalGrossAmount.amount;
        //         console.log("Calculating Booking Services after room", amount);
        //
        //         this.booking.services.forEach((service) => {
        //             console.log(
        //                 "Calculating Booking Services",
        //                 service,
        //                 amount
        //             );
        //             amount +=
        //                 this.booking.rooms * service.totalAmount.grossAmount;
        //         });
        //
        //         if (this.booking.offer.hasOwnProperty("cityTaxes")) {
        //             this.booking.offer.cityTaxes.forEach((tax) => {
        //                 console.log(
        //                     "Calculating Booking CityTaxes",
        //                     tax,
        //                     amount
        //                 );
        //                 amount += tax.totalGrossAmount.amount;
        //             });
        //         }
        //         return amount;
        //     }
        //     return 0;
        // },
        payment_currency() {
            if (this.booking.offer) {
                return this.booking.offer.totalGrossAmount.currency;
            }
            return "EUR";
        },
        browserInfo() {
            const date = new Date();
            const offset = date.getTimezoneOffset();
            return {
                acceptHeader:
                    "text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8",
                language: navigator.language,
                colorDepth: screen.colorDepth,
                javaEnabled: false,
                screenHeight: screen.height,
                screenWidth: screen.width,
                timeZoneOffset: offset,
                userAgent: navigator.userAgent,
            };
        },
        hasPaymentPending() {
            return this.$route.query.hasOwnProperty("redirectResult")
                ? true
                : false;
        },
    },
    methods: {
        ...mapActions({
            setBookingDraftPayment: "setBookingDraftPayment",
            setLoading: "setLoading",
        }),
        async startPayment() {
            this.payment_starting = true;
            await this.loadPaymentMethods();
            this.start_payment = true;
        },
        async handlePayment(state, dropin) {
            this.submitted_payment_method_type =
                state.data.paymentMethod?.type ?? null;
            if (this.submitted_payment_method_type) {
                sessionStorage.setItem(
                    "adyen.submittedMethodType." + this.booking.uuid,
                    this.submitted_payment_method_type
                );
            }
            await axios
                .post("bookings/payment", {
                    amount: (this.payment_amount * 100).toFixed(0),
                    currency: this.payment_currency,
                    paymentMethod: state.data.paymentMethod,
                    storePaymentMethod: state.data.storePaymentMethod,
                    reference: this.booking.uuid,
                    property: this.booking.property,
                    browserInfo: this.browserInfo,
                    shopperEmail: this.booking.booker.email,
                    booker_country: this.booking.booker.countryCode,
                    shopperReference: this.profile.hasOwnProperty("uuid")
                        ? this.profile.uuid
                        : null,
                    // Server-side verification tokens (cannot be manipulated)
                    offer_token: this.booking.offer_token,
                    offer_index: this.booking.offer_index ?? 0,
                    rooms: this.booking.rooms,
                    services_token: this.booking.services_token || null,
                    selected_service_ids: this.booking.services?.map(
                        (s) => s.service?.id
                    ) || [],
                })
                .then((response) => {
                    console.log(response);
                    this.handlePaymentResult(dropin, response);
                })
                .catch((error) => {
                    console.error('Payment error:', error.response?.data);
                    if (error.response?.status === 422) {
                        this.payment_error = true;
                        this.errors = error.response.data.errors || {};
                        if (error.response.data.message) {
                            this.put_booking_errors = [error.response.data.message];
                        }
                    }
                    if (dropin) {
                        dropin.setStatus('ready');
                    }
                })
                .then(() => {
                    this.loading = false;
                });
        },
        async putBooking() {
            this.setLoading(true);
            this.put_booking_errors = [];
            let success = false;
            await axios
                .put("bookings", this.booking)
                .then((response_booking) => {
                    console.log(response_booking);
                    success = true;
                    this.$emit("completed", response_booking.data);
                })
                .catch((error_booking) => {
                    const data = error_booking.response?.data;
                    if (data && data.hasOwnProperty("original_exception")) {
                        if (data.original_status_code === 422) {
                            this.put_booking_errors = data.original_body.messages;
                        } else {
                            this.put_booking_errors.push("unknown_error");
                        }
                    } else if (data && data.message) {
                        // Surface server-side guard messages (e.g. expired offer,
                        // missing payment) so a captured-but-unbooked payment is
                        // visible and the guest can retry instead of the flow
                        // hanging silently.
                        this.put_booking_errors.push(data.message);
                    } else {
                        this.put_booking_errors.push("unknown_error");
                    }
                    console.log("put_booking_errors", this.put_booking_errors);
                })
                .then(() => {
                    this.setLoading(false);
                });
            return success;
        },
        async loadPaymentMethods() {
            this.loading = true;
            this.put_booking_errors = [];
            console.log("booking draft", this.booking.property.uuid);
            await axios
                .post("bookings/payment_methods", {
                    property: this.booking.property.uuid,
                    amount: this.payment_amount,
                    shopperLocale: this.currentLocale.locale,
                    booker_country: this.booking.booker.countryCode,
                    // Lets the server reject a stale session before the widget is
                    // rendered (offer cache expired / cleared on deploy), so the
                    // guest cannot pay into a dead session.
                    offer_token: this.booking.offer_token,
                    shopperReference: this.profile.hasOwnProperty("uuid")
                        ? this.profile.uuid
                        : null,
                })
                .then((response) => {
                    console.log(response);
                    this.payment_uuid = response.data.payment_uuid;
                    this.payment_methods = response.data.payment_methods;

                    this.createDropin(); //, this, 100, 'EUR')
                })
                .catch((error) => {
                    const data = error.response?.data;
                    if (error.response?.status === 422) {
                        this.errors = data?.errors || {};
                        // Surface the session-expired message; the widget is
                        // intentionally not mounted so the guest cannot pay.
                        if (data?.message) {
                            this.put_booking_errors = [data.message];
                        }
                    }
                    this.payment_starting = false;
                })
                .then(() => {
                    this.loading = false;
                });
        },
        async finalizeBooking(payment_data) {
            console.log("finalize booking");
            // Recover the payment method type captured before a redirect: async
            // methods (PayPal / Pay by Bank) may not echo it back in
            // additionalData, and after a full-page return it lives only in
            // sessionStorage.
            if (!this.submitted_payment_method_type) {
                this.submitted_payment_method_type = sessionStorage.getItem(
                    "adyen.submittedMethodType." + this.booking.uuid
                );
            }
            if (this.submitted_payment_method_type) {
                payment_data.submittedPaymentMethodType =
                    this.submitted_payment_method_type;
            }
            this.setBookingDraftPayment(payment_data);
            const booked = await this.putBooking();
            if (booked) {
                // Reservation created — clear the payment-method bridge.
                sessionStorage.removeItem(
                    "adyen.submittedMethodType." + this.booking.uuid
                );
            } else {
                // Payment was captured but the reservation could not be created.
                // The backend has persisted the booking and alerted support, so
                // stop the Drop-in spinner and show a clear message. We do NOT
                // offer a retry here — the card is already charged and re-paying
                // would double-charge the guest.
                this.booking_failed_after_payment = true;
                this.payment_pending = false;
                this.put_booking_errors = [];
                this.payment_refused = false;
                this.payment_error = false;
                if (this.payment_dropin) {
                    this.payment_dropin.setStatus("error", {
                        message: this.$t(
                            "new_reservation.payment.booking_failed_after_payment"
                        ),
                    });
                }
            }
        },
        async loadPaymentDetails(payload) {
            console.log("loadpaymentdetails payload", payload);
            this.setLoading(true);
            this.payment_pending = true;
            await axios
                .post("bookings/payment_details", {
                    property: this.booking.property.uuid,
                    offer_token: this.booking.offer_token,
                    ...payload,
                })
                .then(async (response) => {
                    // Await the whole result handling — including finalizeBooking →
                    // putBooking — so the processing banner stays up through the
                    // booking call instead of being cleared while it still runs.
                    await this.handlePaymentResult(this.payment_dropin, response);
                    console.log("loadPaymentDetails", response.data);
                })
                .catch((error) => {
                    this.payment_pending = false;
                    this.payment_error = true;
                    const data = error.response?.data;
                    if (data?.message) {
                        this.put_booking_errors = [data.message];
                    }
                    this.startPayment();
                    console.log("loadPaymentDetails", error.response);
                })
                .then(() => {
                    // payment_pending is owned by handlePaymentResult / finalizeBooking
                    // (kept up during finalize, cleared on a terminal state).
                    this.setLoading(false);
                });
        },
        async handlePaymentResult(dropin, response) {
            this.payment_refused_reason = null;
            this.payment_refused = false;
            this.payment_error = false;
            const data = response.data || {};
            const resultCode = data.resultCode;
            this.payment_result = resultCode;
            console.log("handlePaymentResult", resultCode);

            // A follow-up action (3DS2 challenge / redirect) must be rendered by
            // the dropin. After a full-page redirect return the dropin is not
            // mounted yet, so (re)initialise it before handing over the action
            // instead of calling handleAction() on a null reference.
            if (data.action) {
                console.log("action present", data);
                // A challenge/redirect is rendered by the dropin — hide the
                // processing banner so it isn't layered over the action UI.
                this.payment_pending = false;
                let activeDropin = dropin || this.payment_dropin;
                if (!activeDropin) {
                    await this.startPayment();
                    activeDropin = this.payment_dropin;
                }
                if (activeDropin) {
                    activeDropin.handleAction(data.action);
                } else {
                    this.payment_error = true;
                }
                return;
            }

            // Treat every non-failure Adyen result that carries a pspReference as
            // a completed payment: create the reservation now and hand Apaleo the
            // pspReference. Apaleo owns the Adyen account and reconciles the
            // asynchronous settlement (PayPal "Pending"/"Received") via its own
            // webhook. Without this, async PayPal results dead-end and the
            // payment is captured with no reservation created.
            const completedCodes = ["Authorised", "Received", "Pending"];
            if (completedCodes.includes(resultCode) && data.pspReference) {
                // Keep the processing banner up through finalizeBooking → putBooking.
                this.payment_pending = true;
                await this.finalizeBooking(data);
                return;
            }

            // "Pending" without a pspReference cannot be linked to a reservation,
            // so keep the shopper waiting rather than dropping the payment.
            if (resultCode === "Pending") {
                this.payment_pending = true;
                const activeDropin = dropin || this.payment_dropin;
                if (activeDropin) {
                    activeDropin.setStatus("loading");
                }
                return;
            }

            // Refused / Cancelled / Error
            this.payment_pending = false;
            this.payment_refused = true;
            this.payment_refused_reason = data.refusalReason;
            const activeDropin = dropin || this.payment_dropin;
            if (activeDropin) {
                activeDropin.setStatus(resultCode);
            } else if (this.start_payment === false) {
                // No widget mounted (redirect return) – re-init so the guest can
                // retry with another method instead of being stuck.
                this.startPayment();
            }
        },
        async handleRedirectResult() {
            console.log("handleRedirectResult onready", this.$route);
            if (this.$route.query.hasOwnProperty("redirectResult")) {
                console.log("ReturnURL present", this.$route.query);
                this.submitted_payment_method_type = sessionStorage.getItem(
                    "adyen.submittedMethodType." + this.booking.uuid
                );
                this.loadPaymentDetails({
                    details: {
                        redirectResult: this.$route.query.redirectResult,
                    },
                });
            }
        },
        async removeStoredPaymentMethod(
            storedPaymentMethodId,
            resolve,
            reject
        ) {
            console.log(storedPaymentMethodId, resolve, reject);
            this.payment_error = null;
            await axios
                .post("bookings/remove_stored_payment_method", {
                    storedPaymentMethodId,
                    property: this.booking.property.uuid,
                })
                .then((response) => {
                    resolve();
                })
                .catch((error) => {
                    this.payment_error =
                        error.data?.error ?? "Error has occured";
                    reject(error.data);
                });
        },
        async createDropin() {
            this.payment_config = {
                paymentMethodsResponse: this.payment_methods,
                clientKey: this.booking.property.payment_frontend_api_key,
                locale: this.currentLocale.locale,
                // setStatusAutomatically: false,
                environment: process.env.MIX_PAYMENT_ENVIRONMENT,
                onSubmit: (state, dropin) => {
                    console.log("payment onSubmit", state, dropin);
                    console.log("this context on submit", this);
                    this.handlePayment(state, dropin);
                },
                //onChange: (state, dropin) => {
                //console.log('payment onChange', state, dropin)
                //},
                onError: (error) => {
                    console.log("adyen error", error);
                },
                onAdditionalDetails: (state, dropin) => {
                    this.loadPaymentDetails(state.data);
                    console.log("payment onAdditionalDetails", state, dropin);
                },
                amount: {
                    value: (this.payment_amount * 100).toFixed(0),
                    currency: this.payment_currency,
                },
                enableRecurring: true,
                shopperReference: this.profile.hasOwnProperty("uuid")
                    ? this.profile.uuid
                    : null,
                shopperInteraction: "Ecommerce",
                recurringProcessingModel: "UnscheduledCardOnFile",
                configuration: {
                    enableStoreDetails: true,
                },
                paymentMethodsConfiguration: {
                    card: {
                        hasHolderName: true,
                        holderNameRequired: true,
                        //name : 'Kreditkarte',
                        enableStoreDetails: true,
                        enableStoreDetails: this.profile.hasOwnProperty("uuid")
                            ? true
                            : false,
                        showRemovePaymentMethodButton: true,
                    },
                },
                allowPaymentMethods: [
                    "scheme",
                    "paypal",
                    "paybybank",
                    "applepay",
                    "paywithgoogle",
                ],
            };
            // console.log("payment config", this.config);

            this.payment_checkout = await AdyenCheckout(this.payment_config);

            this.payment_dropin = this.payment_checkout
                .create("dropin", {
                    // Starting from version 4.0.0, Drop-in configuration only accepts props related to itself and cannot contain generic configuration like the onSubmit event.
                    openFirstPaymentMethod: false,
                    threeDS2: {
                        challengeWindowSize: "05",
                    },
                    showRemovePaymentMethodButton: true,
                    onDisableStoredPaymentMethod:
                        this.removeStoredPaymentMethod,
                    onReady: () => {
                        this.payment_starting = false;
                        this.$scrollTo("#payment_container", 200);
                    },
                })
                .mount(this.$refs.adyen);
        },
    },
    props: {
        current_step: {
            required: true,
            default: 5,
        },
        //booking: {
        //required: true,
        //type: Object,
        //},
    },
    mounted() {
        this.$scrollTo("#payment_container", 1000);
        this.$emit("step", 5);
        if (this.hasPaymentPending === false) {
            this.payment_pending = false;
            this.startPayment();
        }
        if (this.hasPaymentPending === true) {
            this.handleRedirectResult();
        }
        console.log("payment module mounted");
    },
};
</script>

<style></style>
